import React, { useState, useEffect } from 'react';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TextHighlightIcon from 'components/icons/TextHighlightIcon/TextHighlightIcon';
import GVTooltip from 'components/lib/GVToolTip/GVTooltip';
import { useSelector, useDispatch } from 'react-redux';

import { colorOptions } from 'pdftron/docManager/differenceColors';
import { ColorId } from '@verify/shared-utils';
import { getHighlightColor, getInspectionId, getInspectionSettings } from 'store/inspection/inspectionSelectors';
import { updateInspectionHighlightColor } from 'store/request/inspections/actions';
import { trackEvent } from 'components/Tracker/TrackerProvider';

import styles from './TextHighlightModal.module.css';

interface TextHighlightModalProps {
  open: boolean;
  onClose: () => void;
}

const TextHighlightModal: React.FC<TextHighlightModalProps> = ({ open, onClose }) => {
  // Get the current highlight color from Redux
  const currentColor: ColorId = useSelector(getHighlightColor);
  const inspectionId = useSelector(getInspectionId);
  const inspectionSettings = useSelector(getInspectionSettings);
  const [selectedColor, setSelectedColor] = useState<ColorId>(currentColor);
  const dispatch = useDispatch();

  // Effect to update the selected color if the store value changes
  useEffect(() => {
    setSelectedColor(currentColor);
  }, [currentColor]);

  const handleColorSelect = (colorId: ColorId) => {
    setSelectedColor(colorId);
  };

  const handleUpdate = () => {
    dispatch(
      updateInspectionHighlightColor(
        { settings: { ...inspectionSettings, highlightColor: selectedColor } },
        inspectionId,
      ),
    );
    trackEvent({
      name: 'highlightColorChanged',
      data: {
        selectedColor,
        inspectionId,
      },
    });
    onClose();
  };

  const handleClose = () => {
    setSelectedColor(currentColor);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: '4px',
          padding: '24px',
          backgroundColor: '#333333',
          color: 'white',
          minWidth: '418px',
        },
      }}
    >
      <DialogTitle sx={{ padding: '0 0 16px 0' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 500 }}>
            Edit Colors
          </Typography>
          <IconButton onClick={handleClose} size="small" sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ padding: '0', overflow: 'visible' }}>
        <Box sx={{ minHeight: '150px' }}>
          <Typography variant="body1" sx={{ mb: 3, fontSize: '14px' }}>
            To edit the color for all results, please select one:
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
              mb: 3,
              justifyContent: 'flex-start',
              overflow: 'visible',
            }}
          >
            {colorOptions.map((color) => (
              <GVTooltip key={color.id} title={color.name} side="bottom" className={styles.tooltip} sideOffset={10}>
                <Box
                  aria-label={color.name}
                  onClick={() => handleColorSelect(color.id)}
                  sx={{
                    position: 'relative',
                    width: '18px',
                    height: '18px',
                    borderRadius: '2px',
                    backgroundColor: color.hex === 'multicolor' ? 'transparent' : color.hex,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: selectedColor === color.id ? '2px solid white' : 'none',
                    '&:hover::after': {
                      content: '""',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      width: '30px',
                      height: '30px',
                      transform: 'translate(-50%, -50%)',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255, 255, 255, 0.15)',
                      pointerEvents: 'none',
                    },
                  }}
                >
                  {color.hex === 'multicolor' && <TextHighlightIcon width="16px" height="16px" />}
                </Box>
              </GVTooltip>
            ))}
          </Box>

          <Typography variant="body2" sx={{ color: '#aaaaaa', mb: 3, fontSize: '12px' }}>
            Note: This impacts only this inspection.
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 2 }}>
            <Button
              variant="text"
              onClick={handleClose}
              sx={{
                color: 'white',
                textTransform: 'none',
                fontSize: '12px',
                padding: '6px 16px',
              }}
            >
              Cancel
            </Button>
            <Button variant="contained" onClick={handleUpdate} color="secondary">
              Update
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TextHighlightModal;
